import React, { useRef, useState } from "react";
import { Play } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/wks7zzwi_Instalros_video_presentacion%20MAIN.mp4";
const POSTER =
  "https://images.pexels.com/photos/11783119/pexels-photo-11783119.jpeg";

export const ShowcaseVideo = () => {
  const { t } = useI18n();
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    setPlaying(true);
    // Defer to next frame so the video element is mounted before we call play()
    requestAnimationFrame(() => {
      const v = videoRef.current;
      if (v) {
        v.play().catch(() => {
          /* autoplay may be blocked; user can still hit native controls */
        });
      }
    });
  };

  return (
    <section
      id="showcase"
      className="relative py-24 lg:py-32 border-t border-white/[0.08]"
      data-testid="showcase-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-8 mb-10">
          <div className="col-span-12 lg:col-span-5">
            <p className="tactical-label" data-testid="showcase-eyebrow">
              {t("landing.showcaseEyebrow")}
            </p>
            <h2 className="mt-4 text-3xl lg:text-5xl font-medium tracking-tight text-white">
              {t("landing.showcaseTitle")}
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:pt-4">
            <p className="text-base text-white/65 leading-relaxed max-w-xl">
              {t("landing.showcaseDesc")}
            </p>
          </div>
        </div>

        <div
          className="relative card-sharp overflow-hidden aspect-video bg-[#0A0D14]"
          data-testid="showcase-video-frame"
        >
          {!playing && (
            <button
              type="button"
              onClick={handlePlay}
              data-testid="showcase-play-btn"
              aria-label="Play IN|STALROS Showcase video"
              className="absolute inset-0 w-full h-full group cursor-pointer"
            >
              <img
                src={POSTER}
                alt="IN|STALROS Showcase preview"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <span
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(5,5,5,0.25) 0%, rgba(5,5,5,0.75) 100%)",
                }}
              />
              <span className="absolute top-6 left-6 tactical-label">
                {t("landing.showcaseTag")}
              </span>
              <span className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#19A7E0]">
                ● LIVE FOOTAGE
              </span>

              <span className="absolute inset-0 flex items-center justify-center">
                <span className="relative flex items-center justify-center">
                  <span className="absolute w-24 h-24 rounded-full bg-[#19A7E0]/20 blur-xl" />
                  <span className="relative w-20 h-20 rounded-full bg-[#19A7E0] flex items-center justify-center shadow-[0_0_40px_rgba(25,167,224,0.55)] group-hover:bg-[#1FB6FF] transition-colors">
                    <Play className="w-7 h-7 text-black fill-black ml-1" />
                  </span>
                </span>
              </span>

              <span className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
                  IN|STALROS · CASE STUDY
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                  HD · ON-SITE
                </span>
              </span>
            </button>
          )}

          {playing && (
            <video
              ref={videoRef}
              data-testid="showcase-video-player"
              className="absolute inset-0 w-full h-full bg-black object-contain"
              src={VIDEO_SRC}
              poster={POSTER}
              controls
              playsInline
              preload="auto"
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default ShowcaseVideo;
